// Core
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { createRoot, type Root } from 'react-dom/client';

// Libraries
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';
import { useTheme, ThemeProvider } from 'styled-components';
import _ from 'lodash';

// Config
import { BREAKPOINTS } from '../../../config/screen';

// Plugins
import {
  currentValuePercentage,
  DASHBOARD_NULLISH_TEXT,
  formatNumber,
} from '../../../plugins/general';

// Types
import { Theme } from '../../../types/theme';
import { Thresholds } from '../../../types/global';

// Components
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../Paragraph';
import { ThresholdErrorTooltip } from '../ThresholdErrorTooltip';

// Component
import {
  Container,
  Element,
  ProgressIndicatorsContainer,
  ProgressThermometer,
  StepsThermometer,
  TextContainer,
  ThermometerAntD,
} from './Thermometer.style';
import { ThermometerProps } from './Thermometer.types';

function Thermometer({
  className,
  decimalPlaces = 0,
  minValue,
  maxValue,
  running = true,
  testId,
  thresholds,
  title,
  unit,
  value,
}: ThermometerProps): JSX.Element | null {
  // Dependencies
  const isMobile: boolean = useMediaQuery({ maxWidth: 375 });
  const isSmallDevice: boolean = useMediaQuery({ maxWidth: 428 });
  const isXsToSm: boolean = useMediaQuery({
    minWidth: 376,
    maxWidth: BREAKPOINTS.sm - 1,
  });
  const isTablet: boolean = useMediaQuery({
    minWidth: BREAKPOINTS.sm,
    maxWidth: BREAKPOINTS.md,
  });
  const { i18n } = useTranslation();
  const theme: Theme = useTheme();

  // Refs
  const thermometerRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const valueRootRef: React.MutableRefObject<Root | null> = useRef<Root | null>(null);
  const valueDivRef: React.MutableRefObject<HTMLElement | null> = useRef<HTMLElement | null>(null);

  // Calculate steps and threshold value based on breakpoint
  const stepsCount: number = useMemo((): number => {
    if (isMobile) return 31;
    if (isXsToSm) return 34;
    if (isTablet) return 39;
    return 43;
  }, [isMobile, isXsToSm, isTablet]);

  // Magic value threshold (proportional to steps)
  const DEFAULT_THRESHOLD_VALUE: number = stepsCount;

  /* ***********************************************************************************************
  ***************************************** METHODS ************************************************
  *********************************************************************************************** */

  // Validates thresholds and returns error message if invalid
  const thresholdValidationError = useMemo((): string | null => {
    if (!thresholds) return null;

    const fieldName: string = title ? `[${title}] ` : '';

    if (
      !_.isNil(thresholds.lowAlarm)
      && !_.isNil(thresholds.lowWarning)
      && thresholds.lowAlarm >= thresholds.lowWarning
    ) {
      return `${fieldName}[lowAlarm] must be less than [lowWarning] `
        + `(lowAlarm: ${thresholds.lowAlarm}, lowWarning: ${thresholds.lowWarning})`;
    }

    if (
      !_.isNil(thresholds.lowAlarm)
      && !_.isNil(thresholds.highWarning)
      && thresholds.lowAlarm >= thresholds.highWarning
    ) {
      return `${fieldName}[lowAlarm] must be less than [highWarning] `
        + `(lowAlarm: ${thresholds.lowAlarm}, highWarning: ${thresholds.highWarning})`;
    }

    if (
      !_.isNil(thresholds.lowAlarm)
      && !_.isNil(thresholds.highAlarm)
      && thresholds.lowAlarm >= thresholds.highAlarm
    ) {
      return `${fieldName}[lowAlarm] must be less than [highAlarm] `
        + `(lowAlarm: ${thresholds.lowAlarm}, highAlarm: ${thresholds.highAlarm})`;
    }

    if (
      !_.isNil(thresholds.lowWarning)
      && !_.isNil(thresholds.highWarning)
      && thresholds.lowWarning >= thresholds.highWarning
    ) {
      return `${fieldName}[lowWarning] must be less than [highWarning] `
        + `(lowWarning: ${thresholds.lowWarning}, highWarning: ${thresholds.highWarning})`;
    }

    if (
      !_.isNil(thresholds.lowWarning)
      && !_.isNil(thresholds.highAlarm)
      && thresholds.lowWarning >= thresholds.highAlarm
    ) {
      return `${fieldName}[lowWarning] must be less than [highAlarm] `
        + `(lowWarning: ${thresholds.lowWarning}, highAlarm: ${thresholds.highAlarm})`;
    }

    if (
      !_.isNil(thresholds.highWarning)
      && !_.isNil(thresholds.highAlarm)
      && thresholds.highWarning >= thresholds.highAlarm
    ) {
      return `${fieldName}[highWarning] must be less than [highAlarm] `
        + `(highWarning: ${thresholds.highWarning}, highAlarm: ${thresholds.highAlarm})`;
    }

    return null;
  }, [thresholds, title]);

  const highAlarm: number | null | undefined = thresholds?.highAlarm;
  const highWarning: number | null | undefined = thresholds?.highWarning;
  const lowAlarm: number | null | undefined = thresholds?.lowAlarm;
  const lowWarning: number | null | undefined = thresholds?.lowWarning;

  const percentage: number = _.isNil(value) || _.isNil(minValue) || _.isNil(maxValue)
    ? 0
    : currentValuePercentage({
      value,
      minValue,
      maxValue,
    });

  // Calculates a threshold value's position relative to a defined range
  const calculateThreshold = useCallback((
    thresholdValue: number | null | undefined,
    minimumValue: number | null | undefined,
    maximumValue: number | null | undefined,
  ): number | null => {
    if (
      !_.isNil(thresholdValue)
      && !_.isNil(minimumValue)
      && !_.isNil(maximumValue)
      && maximumValue !== minimumValue
    ) {
      const clampedValue: number = Math.max(
        Math.min(thresholdValue, maximumValue),
        minimumValue,
      );
      const relativeValue: number = clampedValue - minimumValue;
      const percent: number = relativeValue / (maximumValue - minimumValue);
      const thresholdPosition: number = Math.round(percent * DEFAULT_THRESHOLD_VALUE);

      // Return null if position is 0 or less (invalid position)
      if (thresholdPosition <= 0) {
        return null;
      }

      return thresholdPosition;
    }

    return null;
  }, [DEFAULT_THRESHOLD_VALUE]);

  const thresholdsTick: Thresholds = useMemo(() => ({
    highWarning: calculateThreshold(thresholds?.highWarning, minValue, maxValue),
    highAlarm: calculateThreshold(thresholds?.highAlarm, minValue, maxValue),
    lowWarning: calculateThreshold(thresholds?.lowWarning, minValue, maxValue),
    lowAlarm: calculateThreshold(thresholds?.lowAlarm, minValue, maxValue),
  }), [
    thresholds,
    minValue,
    maxValue,
    calculateThreshold,
  ]);

  // Determines the current thermometer color based on calculated percentages
  let currentColor: string;

  if (!running || thresholdValidationError) {
    currentColor = theme.color.speedometer.offProgressBackgroundColor;
  } else if (
    !_.isNil(value)
    && (
      (!_.isNil(highAlarm) && value >= highAlarm)
      || (!_.isNil(lowAlarm) && value <= lowAlarm)
    )
  ) {
    currentColor = theme.color.speedometer.alarmBackgroundColor;
  } else if (
    !_.isNil(value)
    && (
      (!_.isNil(highWarning) && value >= highWarning)
      || (!_.isNil(lowWarning) && value <= lowWarning)
    )
  ) {
    currentColor = theme.color.speedometer.warningBackgroundColor;
  } else {
    currentColor = theme.color.speedometer.normalBackgroundColor;
  }

  /* ***********************************************************************************************
  **************************************** COMPONENT HANDLING **************************************
  *********************************************************************************************** */

  // UseEffect to re-render on language change
  useEffect((): void => {
    // Force update component when language changes
  }, [i18n.language]);

  // Cleanup: unmount React root when component unmounts
  useEffect(() => () => {
    if (valueRootRef.current) {
      // Use setTimeout to avoid unmounting during React render
      setTimeout(() => {
        if (valueRootRef.current) {
          valueRootRef.current.unmount();
          valueRootRef.current = null;
        }
      }, 0);
    }
  }, []);

  // Create values containers for minValue, maxValue, and value with their corresponding classes.
  useEffect((): void => {
    const thermometerElement: HTMLElement | null = thermometerRef.current;
    if (thermometerElement) {
      const allStepsItems: NodeListOf<Element> = thermometerElement.querySelectorAll(
        '.ant-progress-steps-item',
      );

      // Clean up existing React root before removing containers
      if (valueRootRef.current) {
        try {
          valueRootRef.current.unmount();
        } catch (error: unknown) {
          // Ignore unmount errors
        }
        valueRootRef.current = null;
      }

      // Remove existing value-containers from all steps first
      const existingValueContainers: NodeListOf<Element> = thermometerElement.querySelectorAll(
        '.value-container',
      );
      existingValueContainers.forEach((container: Element) => {
        container.remove();
      });

      // Reset div ref when removing containers
      valueDivRef.current = null;

      // Finding the element for the current value in the middle of the steps
      const middleStepIndex: number = Math.floor(allStepsItems.length / 2);
      const middleStep: Element = allStepsItems[middleStepIndex];
      const valueDiv: HTMLElement = document.createElement('div');
      valueDiv.classList.add('value-container');
      middleStep.appendChild(valueDiv);
      valueDivRef.current = valueDiv;

      // If there's a threshold validation error, render the error tooltip
      if (thresholdValidationError) {
        // Create root for error tooltip
        valueRootRef.current = createRoot(valueDiv);
        valueRootRef.current.render(
          <ThemeProvider theme={theme}>
            <ThresholdErrorTooltip errorMessage={thresholdValidationError} />
          </ThemeProvider>,
        );
      } else {
        // Normal value rendering
        const displayValue: string = _.isNil(value)
          ? DASHBOARD_NULLISH_TEXT
          : `${formatNumber(value, decimalPlaces)}${unit}`;
        valueDiv.textContent = displayValue;
      }

      valueDivRef.current = valueDiv;
    }
  }, [
    decimalPlaces,
    i18n.language,
    stepsCount,
    theme,
    thresholdValidationError,
    unit,
    value,
  ]);

  useEffect((): void => {
    const thermometerElement: HTMLElement | null = thermometerRef.current;
    if (thermometerElement) {
      const allStepsItems: NodeListOf<Element> = thermometerElement.querySelectorAll(
        '.ant-progress-steps-item',
      );

      if (allStepsItems.length >= 2) {
        // Last step
        const getLastStep: Element = allStepsItems[allStepsItems.length - 2];
        if (getLastStep) {
          // Remove existing max-value-container from all steps first
          const existingMaxContainers: NodeListOf<Element> = thermometerElement.querySelectorAll(
            '.max-value-container',
          );
          existingMaxContainers.forEach((container: Element) => {
            container.remove();
          });

          const newMaxValueDiv: HTMLElement = document.createElement('div');
          newMaxValueDiv.classList.add('max-value-container');
          const maxValueText: Text = document.createTextNode(
            _.isNil(maxValue)
              ? DASHBOARD_NULLISH_TEXT
              : formatNumber(maxValue, decimalPlaces),
          );
          newMaxValueDiv.appendChild(maxValueText);
          getLastStep.appendChild(newMaxValueDiv);
        }

        // First step
        const getFirstStep: Element = allStepsItems[1];
        if (getFirstStep) {
          // Remove existing min-value-container from all steps first
          const existingMinContainers: NodeListOf<Element> = thermometerElement.querySelectorAll(
            '.min-value-container',
          );
          existingMinContainers.forEach((container: Element) => {
            container.remove();
          });

          const newMinValueDiv: HTMLElement = document.createElement('div');
          newMinValueDiv.classList.add('min-value-container');
          const minValueText: Text = document.createTextNode(
            _.isNil(minValue)
              ? DASHBOARD_NULLISH_TEXT
              : formatNumber(minValue, decimalPlaces),
          );
          newMinValueDiv.appendChild(minValueText);
          getFirstStep.appendChild(newMinValueDiv);
        }
      }
    }
  }, [
    decimalPlaces,
    minValue,
    maxValue,
    i18n.language,
    stepsCount,
  ]);

  useEffect((): void => {
    const thermometerElement: HTMLElement | null = thermometerRef.current;
    if (thermometerElement) {
      // Remove all threshold classes from all steps first
      const allStepsItems: NodeListOf<Element> = thermometerElement.querySelectorAll(
        '.ant-progress-steps-item',
      );
      allStepsItems.forEach((step: Element) => {
        step.classList.remove(
          'high-alarm',
          'high-warning',
          'low-alarm',
          'low-warning',
        );
      });

      // Add classes only to valid threshold positions (> 0)
      if (!_.isNil(thresholdsTick.highAlarm) && thresholdsTick.highAlarm > 0) {
        const highAlarmIndicator: HTMLElement | null = thermometerElement.querySelector(
          `.ant-progress-steps-item:nth-child(${thresholdsTick.highAlarm})`,
        );
        highAlarmIndicator?.classList.add('high-alarm');
      }

      if (!_.isNil(thresholdsTick.highWarning) && thresholdsTick.highWarning > 0) {
        const highWarningIndicator: HTMLElement | null = thermometerElement.querySelector(
          `.ant-progress-steps-item:nth-child(${thresholdsTick.highWarning})`,
        );
        highWarningIndicator?.classList.add('high-warning');
      }

      if (!_.isNil(thresholdsTick.lowAlarm) && thresholdsTick.lowAlarm > 0) {
        const lowAlarmIndicator: HTMLElement | null = thermometerElement.querySelector(
          `.ant-progress-steps-item:nth-child(${thresholdsTick.lowAlarm})`,
        );
        lowAlarmIndicator?.classList.add('low-alarm');
      }

      if (!_.isNil(thresholdsTick.lowWarning) && thresholdsTick.lowWarning > 0) {
        const lowWarningIndicator: HTMLElement | null = thermometerElement.querySelector(
          `.ant-progress-steps-item:nth-child(${thresholdsTick.lowWarning})`,
        );
        lowWarningIndicator?.classList.add('low-warning');
      }
    }
  }, [thresholdsTick]);

  return (
    <div className={className} ref={thermometerRef}>
      <Element
        data-testid={testId}
        className={className}
        value={value}
        thresholds={thresholds}
        running={running}
      >
        <Container data-testid={testId ? `${testId}-container` : undefined}>
          <ProgressIndicatorsContainer data-testid={testId ? `${testId}-progress-container` : undefined}>
            <ProgressThermometer>
              <ThermometerAntD
                strokeLinecap="square"
                percent={percentage}
                showInfo={false}
                strokeColor={currentColor}
              />
            </ProgressThermometer>
            <StepsThermometer
              threshold={thresholdsTick}
              running={running}
              hasValidationError={!!thresholdValidationError}
              data-testid={testId ? `${testId}-steps` : undefined}
            >
              <ThermometerAntD
                showInfo={false}
                size="small"
                steps={stepsCount}
              />
            </StepsThermometer>
          </ProgressIndicatorsContainer>
          <TextContainer data-testid={testId ? `${testId}-title` : undefined}>
            <Paragraph
              size={isSmallDevice ? ParagraphSize.xs : ParagraphSize.sm}
              weight={ParagraphWeight.bold}
              color={theme.color.thermometer.titleTextColor}
            >
              {title}
            </Paragraph>
          </TextContainer>
        </Container>
      </Element>
    </div>
  );
}

export { Thermometer };
